import { useEffect, useState, useCallback } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import PropTypes from "prop-types";
import { Spinner } from "@wordpress/components";
import Pagination from "./Pagination.component";
import MediaCard from "./MediaCard.component";
import { Fancybox } from "@fancyapps/ui";
import { fetchData } from "../api";
import Constants from "../constants";
import NoDataMsg from "./NoDataMsg.component";
import MediaCardStyleOne from "./MediaCardStyleOne.component";
import BSHelper from "../BSHelper";

const FANCYBOX_SELECTOR =
	'a[href*=".jpg"],a[href*=".jpeg"],a[href*=".png"],a[href*=".gif"],a[href*=".pdf"],a[href*=".mp4"],a[href*=".mp3"],a[href*=".wav"],a[href*=".ogg"],a[href*=".m4a"],a[href*="youtube.com"]';

const FANCYBOX_IMAGE_SELECTOR =
	'a[href*=".jpg"],a[href*=".jpeg"],a[href*=".png"],a[href*=".gif"]';

const getErrorMessage = (error, fallbackMessage) => {
	if (typeof error === "string") {
		return error;
	}

	if (error?.message) {
		return error.message;
	}

	return fallbackMessage;
};

function MediaList({
	filterType,
	lightboxGroupName,
	sectionStyleType,
	currentPostId,
}) {
	const [itemsList, setItemsList] = useState([]);
	const [itemsCount, setItemsCount] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const itemsPerPage = 10;
	const noResultMsg = __("Sorry, no results found.", Constants.TEXT_DOMAIN);
	const handleApiResponse = (response) => {
		const isImageGroup = filterType === "image_group";
		const responseData = response?.data ?? {};

		if (isImageGroup) {
			const imageGroupItem = responseData?.data?.[0] ?? null;
			const imageGroup = Array.isArray(imageGroupItem?.meta_data?.image_group)
				? imageGroupItem.meta_data.image_group
				: [];

			setItemsList(imageGroupItem);
			setItemsCount(imageGroup.length);
			setTotalPages(responseData?.pagination?.total_pages ?? 1);

			if (imageGroup.length === 0) {
				setError({ message: noResultMsg });
			}

			return;
		}

		const list = Array.isArray(responseData?.data) ? responseData.data : [];
		const count =
			typeof responseData?.count === "number"
				? responseData.count
				: list.length;

		setItemsList(list);
		setItemsCount(count);
		setTotalPages(responseData?.pagination?.total_pages ?? 1);

		if (count === 0) {
			setError({ message: noResultMsg });
		}
	};

	const fetchItemsList = useCallback(
		async (page = 1) => {
			setLoading(true);
			setError(null);

			try {
				let apiURL = `/bseitwp/v1/libraries${
					filterType !== "all" ? "/filter/" + filterType : ""
				}?options[paginate]=1&options[limit]=${itemsPerPage}&options[page]=${page}`;

				if (filterType === "image_group") {
					apiURL = `/bseitwp/v1/libraries/${currentPostId}`;
				}

				const response = await fetchData(apiURL);
				handleApiResponse(response);
			} catch (error) {
				setError(error);
			} finally {
				setLoading(false);
			}
		},
		[filterType, currentPostId],
	);

	// Fancybox config for gallery
	useEffect(() => {
		const isImageGroup = filterType === "image_group";
		const isRTL = BSHelper.isPageRTL();
		const activeSelector = isImageGroup
			? FANCYBOX_IMAGE_SELECTOR
			: FANCYBOX_SELECTOR;

		Fancybox.bind(activeSelector, {
			groupAll: true,
			Carousel: {
				rtl: isRTL,
			},
			video: {
				autoStart: false,
			},
			Html: {
				audio: {
					controls: true,
					autoplay: false,
				},
			},
		});

		return () => {
			Fancybox.unbind(activeSelector);
			Fancybox.close();
		};
	}, [filterType]);

	useEffect(() => {
		setCurrentPage(1);
		setItemsList([]);
		setItemsCount(0);
		setTotalPages(1);
		setError(null);
	}, [filterType, currentPostId]);

	// Fetch items list on mount
	useEffect(() => {
		fetchItemsList(currentPage);
	}, [fetchItemsList, currentPage]);

	const handlePageChange = (page) => {
		setCurrentPage(page);
	};

	const renderItems = () => {
		if (filterType === "image_group") {
			if (!itemsList?.meta_data?.image_group?.length) {
				return null;
			}

			return itemsList.meta_data.image_group.map((imageUrl, index) => {
				return (
					<MediaCard
						lightboxGroupName={lightboxGroupName}
						key={`${
							itemsList.id || itemsList.link || "image-group"
						}-${imageUrl}-${index}`}
						mediaType={itemsList.meta_data.item_type}
						title={BSHelper.truncate(itemsList.title, 60)}
						description={BSHelper.truncate(
							itemsList.meta_data.short_description,
							140,
						)}
						thumbnailUrl={imageUrl}
						mediaUrl={imageUrl}
						isSinglePageImage={true}
					/>
				);
			});
		} else {
			if (!Array.isArray(itemsList)) {
				return null;
			}

			return itemsList.map((item, index) => {
				const itemKey =
					item.id || item.link || `${item.title}-${item.created_date}-${index}`;

				if (sectionStyleType === "styleOne") {
					return (
						<MediaCardStyleOne
							key={itemKey}
							mediaType={item.meta_data.item_type}
							title={BSHelper.truncate(item.title, 60)}
							description={BSHelper.truncate(
								item.meta_data.short_description,
								140,
							)}
							thumbnailUrl={item.thumbnail}
							mediaUrl={
								item.meta_data[item.meta_data.item_type] ||
								item.meta_data["youtube_url"]
							}
							lightboxGroupName={lightboxGroupName}
							createdDate={
								BSHelper.isPageRTL()
									? BSHelper.formatDate(new Date(item.created_date), "ar-EG")
									: BSHelper.formatDate(new Date(item.created_date))
							}
							mediaSinglePageUrl={item.link}
							isSinglePageImage={false}
						/>
					);
				} else {
					return (
						<MediaCard
							lightboxGroupName={lightboxGroupName}
							key={itemKey}
							mediaType={item.meta_data.item_type}
							title={BSHelper.truncate(item.title, 60)}
							description={BSHelper.truncate(
								item.meta_data.short_description,
								140,
							)}
							thumbnailUrl={item.thumbnail}
							mediaUrl={
								item.meta_data[item.meta_data.item_type] ||
								item.meta_data["youtube_url"]
							}
							mediaSinglePageUrl={item.link}
							isSinglePageImage={false}
						/>
					);
				}
			});
		}
	};

	return (
		<>
			<div className="pt-8 md:px-5 lg:px-0">
				{loading && (
					<div className="flex justify-center items-center w-full min-h-80">
						<Spinner
							style={{
								height: "calc(2px * 20)",
								width: "calc(2px * 20)",
							}}
						/>
					</div>
				)}

				{/**
				 * Renders the list of items as a grid of MediaCard components.
				 *
				 * @return {JSX.Element} The grid of MediaCard components.
				 */}
				{!loading && (
					<>
						{itemsCount > 0 && (
							<div className="grid grid-cols-1 gap-5 items-center md:grid-cols-2 lg:grid-cols-3 min-h-80">
								{renderItems()}
							</div>
						)}

						{error && (
							<div className="flex justify-center items-center w-full min-h-80">
								<NoDataMsg
									showImage={true}
									showTitle={false}
									showMsg={true}
									showButton={false}
									msg={getErrorMessage(error, noResultMsg)}
									imageSrc="#"
								/>
							</div>
						)}
					</>
				)}

				{/**
				 * Renders the pagination component if there are more items than the items per page.
				 *
				 * @return {JSX.Element|null} The pagination component or null if there are no more items.
				 */}
				{totalPages > 1 && (
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={handlePageChange}
					/>
				)}
			</div>
		</>
	);
}

MediaList.propTypes = {
	filterType: PropTypes.string.isRequired,
	lightboxGroupName: PropTypes.string.isRequired,
	sectionStyleType: PropTypes.string,
	currentPostId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default MediaList;
